import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';

import Message from '../components/Message';
import Loader from '../components/Loader';

import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useCreateOpeningStockMutation } from '../slices/openingStocksApiSlice';
import { useGetSuppliersQuery } from '../slices/suppliersApiSlice';

import { toast } from 'react-toastify';

const OpeningStocksForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // NORMALIZER — ensures OpeningStocksAddScreen items work
  const normalizeCartItem = (item) => ({
    ...item,
    product: item.product || item._id, // unify ID
    images: Array.isArray(item.images) ? item.images : [item.images], // ensure array
    countInStock: item.countInStock || 9999, // safe fallback
  });

  // RAW ITEMS FROM REDUX
  const rawCartItems = useSelector((state) => state.cart.cartItems || []);

  // NORMALIZED ITEMS (prevents infinite loops)
  const cartItems = useMemo(() => {
    return rawCartItems.map(normalizeCartItem);
  }, [rawCartItems]);

  const [supplierSearch, setSupplierSearch] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [note, setNote] = useState('');
  const [openingStockDate, setOpeningStockDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [expiryDate, setExpiryDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const [costs, setCosts] = useState({});
  const [totalCost, setTotalCost] = useState(0);

  const {
    data: suppliersData,
    isLoading,
    error,
  } = useGetSuppliersQuery({ keyword: supplierSearch });

  const [
    createOpeningStock,
    { isLoading: savingOpeningStocks, error: openingStocksError },
  ] = useCreateOpeningStockMutation();

  // INITIAL COST CALCULATION
  useEffect(() => {
    if (!cartItems.length) return;

    const initialCosts = cartItems.reduce((acc, item) => {
      acc[item.product] = item.qty * item.price * 0.45;
      return acc;
    }, {});

    setCosts(initialCosts);
    setTotalCost(Object.values(initialCosts).reduce((acc, cost) => acc + cost, 0));
  }, [cartItems]);

  // FILTER SUPPLIERS
  useEffect(() => {
    if (suppliersData?.suppliers) {
      const filtered = suppliersData.suppliers.filter((supplier) =>
        supplier.name.toLowerCase().includes(supplierSearch.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [supplierSearch, suppliersData]);

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setFilteredSuppliers([]);
  };

  // COST ADJUSTMENT
  const adjustCostHandler = (productId, adjustment) => {
    const updatedCosts = { ...costs };
    updatedCosts[productId] = Math.max(0, (updatedCosts[productId] || 0) + adjustment);
    setCosts(updatedCosts);

    setTotalCost(Object.values(updatedCosts).reduce((acc, cost) => acc + cost, 0));
  };

  // QTY CHANGE
  const addToCartHandler = (item, qty) => {
    dispatch(addToCart({ ...item, qty }));
  };

  const removeFromCartHandler = (productId) => {
    dispatch(removeFromCart(productId));

    const updatedCosts = { ...costs };
    delete updatedCosts[productId];
    setCosts(updatedCosts);

    setTotalCost(Object.values(updatedCosts).reduce((acc, cost) => acc + cost, 0));
  };

  // PROFIT + UNIT COST HELPERS
  const calculateItemProfit = (totalPrice, totalCost) =>
    (totalPrice - (totalCost || 0)).toFixed(2);

  const calculateItemUnitCost = (totalCost, qty) =>
    qty > 0 ? ((totalCost || 0) / qty).toFixed(2) : 0;

  // SUBMIT HANDLER
  const placeOpeningStockHandler = async () => {
    try {
      if (!selectedSupplier) {
        toast.error('Please select a supplier');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      const openingStocksData = {
        openingStockItems: cartItems.map((item) => ({
          name: item.name,
          qty: item.qty,
          price: item.price,
          itemCost: costs[item.product],
          images: item.images,
          product: item.product,
        })),
        supplierDetails: selectedSupplier._id,
        note,
        date: openingStockDate,
        expiryDate,
        costs,
        status: 'Completed',
      };

      await createOpeningStock(openingStocksData).unwrap();

      dispatch(clearCartItems());
      toast.success('OpeningStock successfully recorded');
      navigate('/house/HomeScreen');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to record OpeningStocks');
    }
  };

  // TOTALS
  const totalQuantity = cartItems.reduce((acc, item) => acc + item.qty, 0);
  const totalPrice = cartItems.reduce(
    (acc, item) => acc + item.qty * item.price,
    0
  );
  const profit = (totalPrice - totalCost).toFixed(2);
  const unitCost =
    totalQuantity > 0 ? (totalCost / totalQuantity).toFixed(2) : 0;

  return (
    <Row>
      <Col md={8}>
        <h1>OpeningStocks Form</h1>

        {cartItems.length === 0 ? (
          <Message>
            Your cart is empty.{' '}
            <Button onClick={() => navigate('/house/HomeScreen')}>
              Go Back
            </Button>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {cartItems.map((item) => (
              <ListGroup.Item key={item.product}>
                <Row>
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Product Thumbnail"
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Col>

                  <Col>{item.name}</Col>

                  <Col md={2}>${item.price}</Col>

                  <Col md={2}>
                    <Form.Control
                      as="select"
                      value={item.qty}
                      onChange={(e) =>
                        addToCartHandler(item, Number(e.target.value))
                      }
                    >
                      {[...Array(item.countInStock).keys()].map((x) => (
                        <option key={x + 1} value={x + 1}>
                          {x + 1}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>

                  <Col md={2}>${(item.qty * item.price).toFixed(2)}</Col>

                  <Col md={1}>
                    <Button
                      variant="light"
                      onClick={() => removeFromCartHandler(item.product)}
                    >
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={4}>Cost:</Col>
                  <Col md={4}>${costs[item.product] || 0}</Col>
                  <Col md={4}>
                    <Button
                      variant="light"
                      onClick={() => adjustCostHandler(item.product, -100)}
                    >
                      <FaMinus />
                    </Button>
                    <Button
                      variant="light"
                      onClick={() => adjustCostHandler(item.product, -10)}
                    >
                      <FaMinus />
                    </Button>
                    <Button
                      variant="light"
                      onClick={() => adjustCostHandler(item.product, -1)}
                    >
                      <FaMinus />
                    </Button>
                    <Button
                      variant="light"
                      onClick={() => adjustCostHandler(item.product, 1)}
                    >
                      <FaPlus />
                    </Button>
                    <Button
                      variant="light"
                      onClick={() => adjustCostHandler(item.product, 10)}
                    >
                      <FaPlus />
                    </Button>
                    <Button
                      variant="light"
                      onClick={() => adjustCostHandler(item.product, 100)}
                    >
                      <FaPlus />
                    </Button>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={6}>
                    <strong>Profit:</strong> $
                    {calculateItemProfit(
                      item.qty * item.price,
                      costs[item.product] || 0
                    )}
                  </Col>
                  <Col md={6}>
                    <strong>Unit Cost:</strong> $
                    {calculateItemUnitCost(costs[item.product] || 0, item.qty)}
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Select Supplier</h2>

              {isLoading ? (
                <Loader />
              ) : error ? (
                <Message variant="danger">
                  {error?.data?.message || 'Failed to load suppliers'}
                </Message>
              ) : (
                <>
                  <Form.Group controlId="supplierSearch">
                    <Form.Label>Search Supplier</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type supplier name"
                      value={supplierSearch}
                      onChange={(e) => setSupplierSearch(e.target.value)}
                    />
                  </Form.Group>

                  {filteredSuppliers.length > 0 && (
                    <ListGroup className="dropdown-menu mt-2">
                      {filteredSuppliers.map((supplier) => (
                        <ListGroup.Item
                          key={supplier._id}
                          action
                          onClick={() => handleSupplierSelect(supplier)}
                        >
                          {supplier.name} ({supplier.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}

                  {selectedSupplier && (
                    <div className="mt-3">
                      <strong>Selected Supplier:</strong>
                      <p>
                        {selectedSupplier.name} ({selectedSupplier.company})<br />
                        Phone: {selectedSupplier.telephone}<br />
                        Address: {selectedSupplier.address}<br />
                        SERIAL: {selectedSupplier.serialNumber}
                      </p>

                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter additional notes"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group controlId="openingStockDate" className="mt-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={openingStockDate}
                          onChange={(e) => setOpeningStockDate(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group controlId="expiryDate" className="mt-2">
                        <Form.Label>Expiry Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={expiryDate}
                          onChange={(e) => setExpiryDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h3>Total Summary:</h3>
              <p>
                <strong>Profit:</strong> ${profit}
              </p>
              <p>
                <strong>TotalRev:</strong> ${totalPrice}
              </p>
            </ListGroup.Item>

            {openingStocksError && (
              <ListGroup.Item>
                <Message variant="danger">
                  {openingStocksError.message}
                </Message>
              </ListGroup.Item>
            )}

            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={cartItems.length === 0 || !selectedSupplier}
                onClick={placeOpeningStockHandler}
              >
                Submit OpeningStocks
              </Button>
              {savingOpeningStocks && <Loader />}
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default OpeningStocksForm;
